import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "src/Funcs/axiosFuncs/axiosInstance";
import { API } from "src/Funcs/APIFuncs/APIFunc";
import { getDesktopBridge, isDesktop } from "src/lib/desktop";

/**
 * Client update checker.
 *
 * Desktop shell with the native updater (new builds): the main process checks
 * the installer feed via electron-updater; this popup drives the user through
 * available → downloading (progress) → "Restart now".
 *
 * Desktop shell without the native updater (old builds) or unsupported
 * packages (e.g. deb): falls back to the release API popup that opens the
 * download link in the user's browser.
 *
 * Plain browser: renders nothing here — proactive update notice is handled by
 * WebUpdateBanner (Service Worker detection + chunk-recovery fallback).
 *
 * Backend: GET /api/app-releases/check (rows managed in Admin Panel → App
 * Releases) + electron-updater feed metadata for the native flow.
 */

const DISMISS_KEY = "appUpdate.dismissedVersion";
const CHECK_DELAY_MS = 5000;

function detectPlatform(bridge) {
  const p = bridge?.versions?.platform;
  if (p === "win32") return "windows";
  if (p === "darwin") return "macos";
  if (p === "linux") return "linux";
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/Win/i.test(ua)) return "windows";
  if (/Mac/i.test(ua)) return "macos";
  if (/Linux/i.test(ua)) return "linux";
  return "";
}

function wasDismissed(version) {
  try {
    return localStorage.getItem(DISMISS_KEY) === version;
  } catch {
    return false;
  }
}

function dismissVersion(version) {
  try {
    localStorage.setItem(DISMISS_KEY, version);
  } catch {
    /* storage may be unavailable */
  }
}

/* Shared popup shell */

function UpdatePopup({ children }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-sm rounded-2xl shadow-2xl p-6"
        style={{ backgroundColor: "var(--white)", border: "1px solid var(--darkGray)" }}
      >
        {children}
      </div>
    </div>
  );
}

export function UpdateIcon() {
  return (
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
      style={{ backgroundColor: "var(--bgPurple)", color: "var(--purple)" }}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    </div>
  );
}

export function PrimaryButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center h-10 px-5 rounded-lg text-[13px] font-semibold text-white transition-all"
      style={{ backgroundColor: "var(--purple)", color: "#fff" }}
      onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.1)")}
      onMouseLeave={(e) => (e.currentTarget.style.filter = "")}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center h-10 px-4 rounded-lg text-[13px] font-medium transition-colors"
      style={{ backgroundColor: "var(--lightGray)", color: "var(--slateGray)" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "var(--darkGray)";
        e.currentTarget.style.color = "var(--black)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "var(--lightGray)";
        e.currentTarget.style.color = "var(--slateGray)";
      }}
    >
      {children}
    </button>
  );
}

/* Native updater flow (electron-updater in the desktop main process) */

function NativeUpdaterFlow() {
  const { t } = useTranslation();
  const bridge = getDesktopBridge();
  const [state, setState] = useState(null);
  const [progressHidden, setProgressHidden] = useState(false);
  /** Set when "Restart now" didn't hand off to the installer in time. */
  const [installStuck, setInstallStuck] = useState(false);

  useEffect(() => {
    if (!bridge?.updater?.onState) return undefined;
    const off = bridge.updater.onState((next) => setState(next ?? null));
    bridge.updater.getState?.().then((s) => setState(s ?? null)).catch(() => {});
    return off;
  }, []);

  if (!bridge?.updater) return null;

  const status = state?.status;
  const version = state?.version;

  if (status === "available" && version && !wasDismissed(version)) {
    return (
      <UpdatePopup>
        <div className="flex items-start gap-3 mb-4">
          <UpdateIcon />
          <div>
            <h3 className="text-[15px] font-bold m-0" style={{ color: "var(--black)" }}>
              {t("appUpdate.title", "New version available")}
            </h3>
            <p className="text-[13px] m-0 mt-1 leading-relaxed" style={{ color: "var(--gray)" }}>
              {t("appUpdate.description", {
                defaultValue:
                  "Version {{version}} of Doitify is available. Update to get the latest features and fixes.",
                version,
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PrimaryButton onClick={() => bridge.updater.download()}>
            {t("appUpdate.update", "Update")}
          </PrimaryButton>
          <SecondaryButton
            onClick={() => {
              dismissVersion(version);
              setState(null);
            }}
          >
            {t("appUpdate.later", "Later")}
          </SecondaryButton>
        </div>
      </UpdatePopup>
    );
  }

  if (status === "downloading" && !progressHidden) {
    const progress = Math.min(100, Math.max(0, Number(state?.progress ?? 0)));
    return (
      <div className="fixed bottom-5 left-5 z-[100] rounded-xl shadow-2xl p-4 w-64" style={{ backgroundColor: "var(--white)", border: "1px solid var(--darkGray)" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[12px] font-semibold" style={{ color: "var(--black)" }}>
            {t("appUpdate.downloading", "Downloading update…")}
          </span>
          <button onClick={() => setProgressHidden(true)} className="text-[12px]" style={{ color: "var(--gray)" }}>✕</button>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "var(--lightGray)" }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: "var(--purple)" }} />
        </div>
        <div className="text-[11px] mt-1 text-right" style={{ color: "var(--gray)" }}>{progress}%</div>
      </div>
    );
  }

  if (status === "downloaded" && version && !wasDismissed(version) && !installStuck) {
    return (
      <UpdatePopup>
        <div className="flex items-start gap-3 mb-4">
          <UpdateIcon />
          <div>
            <h3 className="text-[15px] font-bold m-0" style={{ color: "var(--black)" }}>
              {t("appUpdate.restartTitle", "Update ready to install")}
            </h3>
            <p className="text-[13px] m-0 mt-1 leading-relaxed" style={{ color: "var(--gray)" }}>
              {t("appUpdate.restartDescription", {
                defaultValue:
                  "Version {{version}} has been downloaded. Restart Doitify to apply the update.",
                version,
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PrimaryButton
            onClick={() => {
              bridge.updater.install();
              // If the app is still alive shortly after, the installer did
              // not take over — fall back to the manual download-link flow.
              setTimeout(() => setInstallStuck(true), 5000);
            }}
          >
            {t("appUpdate.restartNow", "Restart now")}
          </PrimaryButton>
          <SecondaryButton
            onClick={() => {
              // Update still installs automatically when the app quits.
              dismissVersion(version);
              setState(null);
            }}
          >
            {t("appUpdate.later", "Later")}
          </SecondaryButton>
        </div>
      </UpdatePopup>
    );
  }

  // Installer hand-off failed → straight to the manual download-link flow.
  if (installStuck) return <ApiUpdateFlow />;

  // checking / idle / not-available / error → fall back to the API popup so
  // unsupported packages (deb) and feeds that are down still notify users.
  if (status === "error" || status === "not-available") {
    return <ApiUpdateFlow />;
  }
  return null;
}

/* API-based flow (release rows managed in the Admin Panel) */

function ApiUpdateFlow() {
  const { t } = useTranslation();
  const [latest, setLatest] = useState(null);
  const [required, setRequired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const bridge = getDesktopBridge();
        const platform = detectPlatform(bridge);
        let version = null;
        if (typeof bridge?.getAppVersion === "function") {
          const res = await bridge.getAppVersion();
          version = res?.appVersion || null;
        }
        if (!platform || !version) return;
        const params = new URLSearchParams({
          app: "desktop",
          platform,
          version,
        });
        const { data } = await axiosInstance.get(`${API}app-releases/check?${params.toString()}`);
        const info = data?.body ?? data;
        if (cancelled || !info?.updateAvailable || !info.latest?.url) return;
        if (!info.updateRequired && wasDismissed(info.latest.version)) return;
        setLatest(info.latest);
        setRequired(!!info.updateRequired);
      } catch {
        /* update check must never break the app */
      }
    }, CHECK_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  if (!latest) return null;

  const openDownload = () => {
    const bridge = getDesktopBridge();
    if (typeof bridge?.openExternal === "function") bridge.openExternal(latest.url);
    else window.open(latest.url, "_blank", "noopener,noreferrer");
  };

  return (
    <UpdatePopup>
      <div className="flex items-start gap-3 mb-4">
        <UpdateIcon />
        <div>
          <h3 className="text-[15px] font-bold m-0" style={{ color: "var(--black)" }}>
            {t("appUpdate.title", "New version available")}
          </h3>
          <p className="text-[13px] m-0 mt-1 leading-relaxed" style={{ color: "var(--gray)" }}>
            {t("appUpdate.description", {
              defaultValue:
                "Version {{version}} of Doitify is available. Update to get the latest features and fixes.",
              version: latest.version,
            })}
          </p>
        </div>
      </div>

      {latest.notes ? (
        <div
          className="text-[12px] leading-relaxed rounded-lg p-3 mb-4 whitespace-pre-line"
          style={{ backgroundColor: "var(--lightGray)", color: "var(--slateGray)" }}
        >
          {latest.notes}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <PrimaryButton onClick={openDownload}>{t("appUpdate.update", "Update")}</PrimaryButton>
        {!required && (
          <SecondaryButton
            onClick={() => {
              dismissVersion(latest.version);
              setLatest(null);
            }}
          >
            {t("appUpdate.later", "Later")}
          </SecondaryButton>
        )}
      </div>
    </UpdatePopup>
  );
}

export default function AppUpdateChecker() {
  const bridge = getDesktopBridge();
  if (bridge?.updater?.onState) return <NativeUpdaterFlow />;
  if (!isDesktop()) return null;
  return <ApiUpdateFlow />;
}
